import React, { useState } from 'react';
import { View, Text, ScrollView, TextInput, TouchableOpacity, Alert, ActivityIndicator, StyleSheet } from 'react-native';
import { Header } from '../../components/Header';
import { useGetSchedulesQuery, useCreateScheduleMutation } from '../../store/api';
import { useTheme } from '../../theme/ThemeContext';
import { COLORS, getThemeStyles, globalStyles } from '../../theme/designSystem';

export const ManageSchedulesScreen = () => {
  const todayStr = new Date().toISOString().split('T')[0];
  const [workDate, setWorkDate] = useState(todayStr);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [breakStart, setBreakStart] = useState('13:00');
  const [breakEnd, setBreakEnd] = useState('14:00');
  const [maxMeetingsPerSlot, setMaxMeetingsPerSlot] = useState('2');

  const { data: schedules = [], isLoading, refetch } = useGetSchedulesQuery();
  const [createScheduleApi, { isLoading: isCreating }] = useCreateScheduleMutation();
  const { isDark } = useTheme();
  const theme = getThemeStyles(isDark);

  const handleCreate = async () => {
    try {
      await createScheduleApi({
        workDate,
        startTime,
        endTime,
        breakStart: breakStart || undefined,
        breakEnd: breakEnd || undefined,
        maxMeetingsPerSlot: parseInt(maxMeetingsPerSlot, 10) || 2,
        isAvailable: true,
      }).unwrap();

      Alert.alert('Schedule Created', `Work schedule for ${workDate} created successfully.`);
      refetch();
    } catch (err: any) {
      Alert.alert('Creation Failed', err?.data?.message || 'Could not create schedule.');
    }
  };

  return (
    <View style={[globalStyles.container, { backgroundColor: theme.bg }]}>
      <Header title="Work Schedule Setup" subtitle="Define available daily slot parameters" />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 32 }}
      >
        {/* Form Card */}
        <View style={[globalStyles.card, { backgroundColor: theme.cardBg, borderColor: theme.border, marginBottom: 20 }]}>
          <Text style={[styles.formTitle, { color: theme.textPrimary }]}>
            ➕ Add / Update Daily Work Hours
          </Text>

          <Text style={[styles.label, { color: theme.textPrimary }]}>Work Date (YYYY-MM-DD)</Text>
          <TextInput
            value={workDate}
            onChangeText={setWorkDate}
            placeholder="YYYY-MM-DD"
            placeholderTextColor={theme.textSecondary}
            style={[
              globalStyles.input,
              {
                backgroundColor: theme.inputBg,
                borderColor: theme.border,
                color: theme.textPrimary,
              },
            ]}
          />

          <View style={styles.row}>
            <View style={{ flex: 1, marginRight: 6 }}>
              <Text style={[styles.label, { color: theme.textPrimary }]}>Start Time (HH:mm)</Text>
              <TextInput
                value={startTime}
                onChangeText={setStartTime}
                placeholder="09:00"
                placeholderTextColor={theme.textSecondary}
                style={[
                  globalStyles.input,
                  {
                    backgroundColor: theme.inputBg,
                    borderColor: theme.border,
                    color: theme.textPrimary,
                  },
                ]}
              />
            </View>
            <View style={{ flex: 1, marginLeft: 6 }}>
              <Text style={[styles.label, { color: theme.textPrimary }]}>End Time (HH:mm)</Text>
              <TextInput
                value={endTime}
                onChangeText={setEndTime}
                placeholder="17:00"
                placeholderTextColor={theme.textSecondary}
                style={[
                  globalStyles.input,
                  {
                    backgroundColor: theme.inputBg,
                    borderColor: theme.border,
                    color: theme.textPrimary,
                  },
                ]}
              />
            </View>
          </View>

          <View style={styles.row}>
            <View style={{ flex: 1, marginRight: 6 }}>
              <Text style={[styles.label, { color: theme.textPrimary }]}>Break Start (HH:mm)</Text>
              <TextInput
                value={breakStart}
                onChangeText={setBreakStart}
                placeholder="13:00"
                placeholderTextColor={theme.textSecondary}
                style={[
                  globalStyles.input,
                  {
                    backgroundColor: theme.inputBg,
                    borderColor: theme.border,
                    color: theme.textPrimary,
                  },
                ]}
              />
            </View>
            <View style={{ flex: 1, marginLeft: 6 }}>
              <Text style={[styles.label, { color: theme.textPrimary }]}>Break End (HH:mm)</Text>
              <TextInput
                value={breakEnd}
                onChangeText={setBreakEnd}
                placeholder="14:00"
                placeholderTextColor={theme.textSecondary}
                style={[
                  globalStyles.input,
                  {
                    backgroundColor: theme.inputBg,
                    borderColor: theme.border,
                    color: theme.textPrimary,
                  },
                ]}
              />
            </View>
          </View>

          <Text style={[styles.label, { color: theme.textPrimary }]}>
            Max Meetings Allowed Per 30m Slot
          </Text>
          <TextInput
            value={maxMeetingsPerSlot}
            onChangeText={setMaxMeetingsPerSlot}
            keyboardType="number-pad"
            style={[
              globalStyles.input,
              {
                backgroundColor: theme.inputBg,
                borderColor: theme.border,
                color: theme.textPrimary,
              },
            ]}
          />

          <TouchableOpacity
            onPress={handleCreate}
            disabled={isCreating}
            style={[globalStyles.buttonPrimary, { marginTop: 8 }]}
          >
            {isCreating ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={globalStyles.buttonText}>Save Work Schedule 💾</Text>
            )}
          </TouchableOpacity>
        </View>

        {/* Existing Schedules List */}
        <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>
          Configured Schedules List
        </Text>
        {schedules.map((item) => (
          <View
            key={item.id}
            style={[styles.schedCard, { backgroundColor: theme.cardBg, borderColor: theme.border }]}
          >
            <View style={{ flex: 1 }}>
              <Text style={[styles.schedDate, { color: theme.textPrimary }]}>
                📅 {item.workDate}
              </Text>
              <Text style={[styles.schedDetails, { color: theme.textSecondary }]}>
                Hours: {item.startTime} - {item.endTime} (Break: {item.breakStart}-{item.breakEnd})
              </Text>
              <Text style={[styles.schedCap, { color: COLORS.primary }]}>
                Slot Capacity: {item.maxMeetingsPerSlot} visitors/slot
              </Text>
            </View>

            <View style={styles.activeBadge}>
              <Text style={styles.activeText}>ACTIVE</Text>
            </View>
          </View>
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  formTitle: {
    fontSize: 16,
    fontWeight: '800',
    marginBottom: 14,
  },
  label: {
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 6,
  },
  row: {
    flexDirection: 'row',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '800',
    marginBottom: 12,
  },
  schedCard: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    marginBottom: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  schedDate: {
    fontSize: 14,
    fontWeight: '800',
  },
  schedDetails: {
    fontSize: 11,
    marginTop: 2,
  },
  schedCap: {
    fontSize: 11,
    fontWeight: '700',
    marginTop: 2,
  },
  activeBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 20,
    backgroundColor: COLORS.successBg,
    borderColor: COLORS.success,
    borderWidth: 1,
  },
  activeText: {
    color: COLORS.success,
    fontSize: 10,
    fontWeight: '800',
  },
});
